import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  chatService,
  messagingService,
  ChatListItem,
  MessageItem,
  CreateChatInput,
  ChatParticipant,
  ChatNotificationData,
} from '@/services/firebase';
import { useMyChats } from '@/api/hooks/useMeetingChats';
import { useMyProfile } from '@/api/hooks/useProfile';
import { playNotificationSound } from '@/utils/notificationSound';
import { useActiveChat } from '@/contexts/ActiveChatContext';

/**
 * Hook do pobierania listy czatów użytkownika
 * Pobiera listę z API i uzupełnia dane z Firebase (ostatnia wiadomość, nieprzeczytane)
 */
export const useChats = () => {
  const [firebaseChats, setFirebaseChats] = useState<ChatListItem[]>([]);
  const [isLoadingFirebase, setIsLoadingFirebase] = useState(true);
  const { data: myProfile } = useMyProfile();
  const { activeChatId } = useActiveChat();
  const userId = myProfile?.id;
  const prevUnreadCountRef = useRef<number>(0);
  
  // Pobierz czaty z API
  const { data: apiChats = [], isLoading: isLoadingApi, error: apiError, refetch } = useMyChats();

  // Subskrybuj Firebase dla real-time updates (ostatnia wiadomość, nieprzeczytane)
  useEffect(() => {
    console.log('useChats - subscribing with userId:', userId);
    
    if (!userId) {
      setFirebaseChats([]);
      setIsLoadingFirebase(false);
      return;
    }

    setIsLoadingFirebase(true);

    try {
      const unsubscribe = chatService.subscribeToChats(
        userId,
        (chatList) => {
          setFirebaseChats(chatList);
          setIsLoadingFirebase(false);
        },
        (err) => {
          console.error('useChats Firebase error:', err);
          setIsLoadingFirebase(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.warn('Firebase not available:', err);
      setFirebaseChats([]);
      setIsLoadingFirebase(false);
      return () => {};
    }
  }, [userId]);

  // Zbiór ID czatów z API - tylko te czaty są wyświetlane
  const apiChatIds = useMemo(() => {
    return new Set(apiChats.map(c => c.firebaseChatId));
  }, [apiChats]);

  // Sprawdź czy są nowe czaty w Firebase, których nie ma w API
  // Jeśli tak - odśwież listę z API
  useEffect(() => {
    if (!userId || firebaseChats.length === 0) return;
    
    const hasNewChats = firebaseChats.some(fc => !apiChatIds.has(fc.id));
    if (hasNewChats) {
      console.log('useChats - detected new chats in Firebase, refetching API...');
      refetch();
    }
  }, [firebaseChats, apiChatIds, userId, refetch]);

  // Łącz dane - wyświetlaj tylko czaty które są w API, ale z danymi real-time z Firebase
  const chats = useMemo(() => {
    console.log('useChats merge - API chats:', apiChats.length, 'Firebase chats:', firebaseChats.length);
    
    if (apiChats.length === 0) {
      return [];
    }

    // Stwórz mapę z Firebase chats
    const firebaseChatsMap = new Map<string, ChatListItem>();
    firebaseChats.forEach(chat => {
      firebaseChatsMap.set(chat.id, chat);
    });

    // Wyświetlaj tylko czaty które są w API
    const mergedChats: ChatListItem[] = apiChats.map(apiChat => {
      const firebaseChat = firebaseChatsMap.get(apiChat.firebaseChatId);
      
      if (firebaseChat) {
        // Mamy dane z Firebase - użyj ich (mają real-time updates)
        return firebaseChat;
      }
      
      // Brak danych z Firebase - stwórz element z danych API
      const isHost = apiChat.hostId === userId;
      const otherUserId = isHost ? apiChat.applicantId : apiChat.hostId;
      
      return {
        id: apiChat.firebaseChatId,
        odataName: 'Czat',
        odataAvatar: '',
        lastMessage: 'Rozpocznij rozmowę...',
        lastMessageTime: new Date(apiChat.updatedAt),
        unreadCount: 0,
        verified: false,
        otherParticipant: {
          odataId: otherUserId,
          odataName: 'Czat',
        },
      } as ChatListItem;
    });

    // Sortuj po czasie ostatniej wiadomości (najnowsze na górze)
    mergedChats.sort((a, b) => b.lastMessageTime.getTime() - a.lastMessageTime.getTime());
    
    return mergedChats;
  }, [apiChats, firebaseChats, userId]);

  const isLoading = isLoadingApi || isLoadingFirebase;
  
  // Oblicz nieprzeczytane pomijając aktywny czat (jeśli jest otwarty)
  const totalUnread = chats.reduce((sum, chat) => {
    // Pomiń aktywny czat przy liczeniu nieprzeczytanych
    if (activeChatId && chat.id === activeChatId) {
      return sum;
    }
    return sum + chat.unreadCount;
  }, 0);

  // Odtwórz dźwięk gdy wzrośnie liczba nieprzeczytanych wiadomości
  // Pomijamy pierwszy render (initial load)
  const isInitialLoadRef = useRef(true);
  
  useEffect(() => {
    // Pomiń gdy jeszcze trwa początkowe ładowanie
    if (isLoading && isInitialLoadRef.current) {
      return;
    }
    
    // Po pierwszym załadowaniu oznacz jako zainicjalizowany
    if (isInitialLoadRef.current && !isLoading) {
      isInitialLoadRef.current = false;
      prevUnreadCountRef.current = totalUnread;
      console.log('useChats - initialized with unread count:', totalUnread);
      return;
    }
    
    // Odtwórz dźwięk gdy liczba nieprzeczytanych wzrosła
    // (działa również podczas refetch gdy isLoading jest false)
    if (totalUnread > prevUnreadCountRef.current) {
      console.log('useChats - playing notification sound, unread changed from', prevUnreadCountRef.current, 'to', totalUnread);
      playNotificationSound();
    }
    prevUnreadCountRef.current = totalUnread;
  }, [totalUnread, isLoading]);

  return {
    chats,
    isLoading,
    error: apiError,
    totalUnread,
    refetch,
  };
};

const MESSAGES_PER_PAGE = 60;

/**
 * Hook do pobierania wiadomości w konwersacji z paginacją
 * Automatycznie oznacza wiadomości jako przeczytane gdy przychodzą nowe i czat jest aktywny
 */
export const useMessages = (chatId: string | undefined) => {
  const [recentMessages, setRecentMessages] = useState<MessageItem[]>([]);
  const [olderMessages, setOlderMessages] = useState<MessageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { data: myProfile } = useMyProfile();
  const userId = myProfile?.id;
  const { activeChatId } = useActiveChat();
  
  // Track active chat in ref for use in subscription callback
  const activeChatIdRef = useRef<string | null>(null);
  activeChatIdRef.current = activeChatId;

  // Połącz starsze i nowsze wiadomości
  const messages = useMemo(() => {
    return [...olderMessages, ...recentMessages];
  }, [olderMessages, recentMessages]);

  useEffect(() => {
    console.log('useMessages - chatId:', chatId, 'userId:', userId);
    
    if (!chatId || !userId) {
      setRecentMessages([]);
      setOlderMessages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setOlderMessages([]);
    setHasMore(true);

    const unsubscribe = chatService.subscribeToMessages(
      chatId,
      userId,
      (messageList) => {
        console.log('useMessages - received messages:', messageList.length);
        
        // Mark as read immediately if this chat is active and latest message is not own
        const isActiveChat = activeChatIdRef.current === chatId;
        const latestMessage = messageList.find(m => m.timestamp && !m.date);
        if (isActiveChat && latestMessage && !latestMessage.isOwn) {
          chatService.markMessagesAsRead(chatId, userId);
        }
        
        setRecentMessages(messageList);
        setIsLoading(false);
      },
      (err) => {
        console.error('useMessages - error:', err);
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [chatId, userId]);

  // Funkcja do ładowania starszych wiadomości
  const loadMoreMessages = useCallback(async () => {
    if (!chatId || !userId || isLoadingMore || !hasMore) return;
    
    // Znajdź najstarszą wiadomość (bez separatorów dat)
    const allMessages = [...olderMessages, ...recentMessages];
    const oldestMessage = allMessages.find(m => m.timestamp && !m.date);
    
    if (!oldestMessage?.timestamp) return;
    
    setIsLoadingMore(true);
    
    try {
      const older = await chatService.getOlderMessages(
        chatId,
        userId,
        oldestMessage.timestamp,
        MESSAGES_PER_PAGE
      );
      
      if (older.length < MESSAGES_PER_PAGE) {
        setHasMore(false);
      }
      
      if (older.length > 0) {
        setOlderMessages(prev => [...older, ...prev]);
      }
    } catch (err) {
      console.error('Error loading older messages:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [chatId, userId, isLoadingMore, hasMore, olderMessages, recentMessages]);

  return {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMoreMessages,
    error,
  };
};

/**
 * Hook do wysyłania wiadomości
 */
export const useSendMessage = () => {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { data: myProfile } = useMyProfile();
  const userId = myProfile?.id;

  const sendMessage = useCallback(
    async (chatId: string, text: string) => {
      console.log('useSendMessage - sending:', { chatId, text, userId });
      if (!userId || !text.trim()) return null;

      setIsSending(true);
      setError(null);

      try {
        const messageId = await chatService.sendMessage(userId, {
          chatId,
          text: text.trim(),
          type: 'text',
        });
        console.log('useSendMessage - sent, messageId:', messageId);
        return messageId;
      } catch (err) {
        console.error('useSendMessage - error:', err);
        setError(err as Error);
        return null;
      } finally {
        setIsSending(false);
      }
    },
    [userId]
  );

  const sendGift = useCallback(
    async (
      chatId: string,
      giftData: { giftId: string; giftName: string; giftEmoji: string; amount: number }
    ) => {
      if (!userId) return null;

      setIsSending(true);
      setError(null);

      try {
        const messageId = await chatService.sendMessage(userId, {
          chatId,
          text: `Wysłano prezent: ${giftData.giftEmoji} ${giftData.giftName}`,
          type: 'gift',
          giftData,
        });
        return messageId;
      } catch (err) {
        setError(err as Error);
        return null;
      } finally {
        setIsSending(false);
      }
    },
    [userId]
  );

  return {
    sendMessage,
    sendGift,
    isSending,
    error,
  };
};

/**
 * Hook do tworzenia/pobierania czatu
 */
export const useCreateChat = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { data: myProfile } = useMyProfile();
  const userId = myProfile?.id;

  const createOrGetChat = useCallback(
    async (input: CreateChatInput, currentUserData: ChatParticipant) => {
      if (!userId) return null;

      setIsCreating(true);
      setError(null);

      try {
        const chatId = await chatService.getOrCreateChat(
          userId,
          currentUserData,
          input
        );
        return chatId;
      } catch (err) {
        setError(err as Error);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [userId]
  );

  return {
    createOrGetChat,
    isCreating,
    error,
  };
};

/**
 * Hook do inicjalizacji FCM i obsługi powiadomień
 */
export const useFirebaseMessaging = (
  onNotificationReceived?: (data: ChatNotificationData) => void,
  onNotificationOpened?: (data: ChatNotificationData) => void
) => {
  const { data: myProfile } = useMyProfile();
  const userId = myProfile?.id;
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Inicjalizacja FCM
  useEffect(() => {
    if (!userId) {
      setIsInitialized(false);
      return;
    }

    const initFCM = async () => {
      const token = await messagingService.initialize(userId);
      setFcmToken(token);
      setIsInitialized(true);

      // Sprawdź początkowe powiadomienie
      const initialNotification = await messagingService.getInitialNotification();
      if (initialNotification && onNotificationOpened) {
        onNotificationOpened(initialNotification);
      }
    };

    initFCM();

    return () => {
      // Cleanup przy odmontowaniu
    };
  }, [userId, onNotificationOpened]);

  // Handler dla powiadomień w foreground
  useEffect(() => {
    if (onNotificationReceived) {
      messagingService.setForegroundHandler(onNotificationReceived);
    }
  }, [onNotificationReceived]);

  // Handler dla otwarcia powiadomienia
  useEffect(() => {
    if (onNotificationOpened) {
      unsubscribeRef.current = messagingService.onNotificationOpenedApp(
        onNotificationOpened
      );
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [onNotificationOpened]);

  // Cleanup przy wylogowaniu
  const cleanup = useCallback(async () => {
    if (userId) {
      await messagingService.cleanup(userId);
      setFcmToken(null);
      setIsInitialized(false);
    }
  }, [userId]);

  return {
    fcmToken,
    isInitialized,
    cleanup,
  };
};

/**
 * Hook do oznaczania wiadomości jako przeczytane
 */
export const useMarkAsRead = () => {
  const { data: myProfile } = useMyProfile();
  const userId = myProfile?.id;

  const markAsRead = useCallback(
    async (chatId: string) => {
      if (!userId || !chatId) return;
      await chatService.markMessagesAsRead(chatId, userId);
    },
    [userId]
  );

  return { markAsRead };
};

